import React from 'react'
import { connect } from 'react-redux'
import {
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  // IconButton,
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@material-ui/core'
import {
  CheckCircle,
  Error,
  HourglassEmpty,
  // Visibility,
  // Delete,
} from '@material-ui/icons'
import { toggleDialog } from '../../actions/view'

// Define styles object
const styles = theme => ({
  complianceContainer: {
    padding: '24px',
    height: '100%',
    backgroundColor: 'white',
    overflowY: 'auto',
  },
  pageTitle: {
    marginBottom: 24,
  },
  sectionTitle: {
    marginTop: 24,
  },
  companyDetails: {
    marginBottom: 24,
  },
  listContainer: {
    marginBottom: 24,
  },
  listItem: {
    padding: 16,
    marginBottom: 0,
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  listItemRoot: {
    borderTop: '1px solid #e0e0e0',
    '&:last-child': {
      borderBottom: '1px solid #e0e0e0',
    },
  },
  listItemContainer: {
    borderTop: '1px solid #e0e0e0',
    '&:last-child': {
      borderBottom: '1px solid #e0e0e0',
    },
  },
  listItemText: {
    flex: '1 1 auto',
  },
  // listItemActions: {
  //   display: 'flex',
  //   alignItems: 'center',
  // },
  actionButton: {
    marginLeft: 8,
  },
  buttonContainer: {
    // marginBottom: 16,
  },
  addButton: {
    marginLeft: 16,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 24,
  },
  warningButton: {
    backgroundColor: '#ff9800', // Material-UI's orange color
    color: 'white',
    '&:hover': {
      backgroundColor: '#f57c00', // Darker orange on hover
    },
  },
  [theme.breakpoints.down('sm')]: {
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '500',
    },
    actionButton: {
      marginLeft: 'auto',
    },
    complianceContainer: {
      padding: '16px',
    },
    listItem: {
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    listItemText: {
      paddingRight: 0,
      marginBottom: 16,
    },
    // listItemActions: {
    //   position: 'relative',
    //   transform: 'none',
    //   display: 'flex',
    //   alignItems: 'center',
    //   width: '100%',
    // },
  },
})

const a2pPolicySid = 'RNb0d4771c2c98518d916a3d4cd70a8f8b'
const cnamPolicySid = 'RNf3db3cd1fe25fcfd3c3ded065c8fea53'
const shakenStirSid = 'RN7a97559effdf62d00f4298208492a5ea'

// Add these constants at the top with the other policy SIDs
const POLICY_FRIENDLY_NAMES = {
  [a2pPolicySid]: 'Messaging Compliance',
  [shakenStirSid]: 'Call Authentication',
  [cnamPolicySid]: 'Caller ID Services',
}

// Add this helper function to determine volume type
const getTrustProductVolumeType = (trustProduct, a2p10dlc, index) => {
  // Look through campaigns to find ones associated with this trust product's brands
  const campaigns = Object.values(a2p10dlc.campaigns || {})
  const brands = Object.values(a2p10dlc.brands || {})

  // Find brand(s) associated with this trust product
  const associatedBrand = brands.find(
    brand => brand.a2pProfileBundleSid === trustProduct.sid,
  )

  if (!associatedBrand) return ''

  // Find all campaigns for this brand
  const brandCampaigns = campaigns.filter(
    campaign => associatedBrand.sid === campaign.brandRegistrationSid,
  )

  // If no campaigns found, return empty string
  if (brandCampaigns.length === 0) return ''

  // Check if ALL campaigns are low volume
  const allLowVolume = brandCampaigns.every(
    campaign => campaign.usAppToPersonUsecase === 'LOW_VOLUME',
  )

  // Check if ANY campaign is NOT low volume (high volume)
  const hasHighVolume = brandCampaigns.some(
    campaign => campaign.usAppToPersonUsecase !== 'LOW_VOLUME',
  )

  if (allLowVolume) return 'Low Volume'
  if (hasHighVolume) return 'High Volume'
  return ''
}

class ManageCompliance extends React.Component {
  state = {
    detailsDialog: {
      open: false,
      data: null,
      type: null, // 'trust-product' or 'use-case'
    },
  }

  formatPhoneNumber(phoneNumber) {
    if (!phoneNumber) return ''
    // Remove all non-digit characters except plus sign
    const cleaned = phoneNumber.replace(/[^\d+]/g, '')

    // Handle +1 prefix
    if (cleaned.startsWith('+1') && cleaned.length === 12) {
      return `+1 (${cleaned.slice(2, 5)}) ${cleaned.slice(
        5,
        8,
      )}-${cleaned.slice(8)}`
    }
    // Handle 10 digit number
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
        6,
      )}`
    }
    // Return original if not matching expected formats
    return phoneNumber
  }

  getNormalizedStatus(status) {
    const statusLower = status?.toLowerCase() || ''
    const statusMap = {
      'twilio-approved': 'Approved',
      'twilio-rejected': 'Rejected',
      'pending-review': 'In Review',
      'in-review': 'In Review',
      in_progress: 'In Review',
      verified: 'Approved',
      failed: 'Rejected',
      waiting: 'Waiting',
    }

    // Check if status starts with any of our known status prefixes
    for (const [key, value] of Object.entries(statusMap)) {
      if (statusLower.startsWith(key)) {
        return value
      }
    }

    return 'Unknown'
  }

  getStatusStyle(status) {
    const normalizedStatus = this.getNormalizedStatus(status)
    switch (normalizedStatus) {
      case 'Approved':
        return { color: 'green' }
      case 'Rejected':
        return { color: 'red' }
      case 'In Review':
        return { color: 'orange' }
      case 'Waiting':
        return { color: 'gray' }
      default:
        return {}
    }
  }

  renderStatusIcon(status) {
    const normalizedStatus = this.getNormalizedStatus(status)

    switch (normalizedStatus) {
      case 'Approved':
        return <CheckCircle style={{ color: 'green', marginLeft: 3 }} />
      case 'Rejected':
        return <Error style={{ color: 'red', marginLeft: 3 }} />
      case 'In Review':
        return <HourglassEmpty style={{ color: 'orange', marginLeft: 3 }} />
      case 'Waiting':
        return <HourglassEmpty style={{ color: 'gray', marginLeft: 3 }} />
      default:
        return null
    }
  }

  handleOpenDetails = (data, type) => {
    this.setState({
      detailsDialog: {
        open: true,
        data,
        type,
      },
    })
  }

  handleCloseDetails = () => {
    this.setState({
      detailsDialog: {
        open: false,
        data: null,
        type: null,
      },
    })
  }

  renderDetailsDialog = () => {
    const { detailsDialog } = this.state
    const { data, type } = detailsDialog

    if (!data) return null

    return (
      <Dialog
        open={detailsDialog.open}
        onClose={this.handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {type === 'trust-product'
            ? POLICY_FRIENDLY_NAMES[data.policySid]
            : data.name || 'Campaign Details'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            <strong>Status:</strong>{' '}
            <span
              style={this.getStatusStyle(data.status || data.campaignStatus)}
            >
              {this.getNormalizedStatus(data.status || data.campaignStatus)}
            </span>
          </Typography>
          {/* Add more details as needed */}
        </DialogContent>
      </Dialog>
    )
  }

  renderCompanyDetails() {
    const {
      customerProfile,
      user_email,
      user_phone_number,
      classes,
      toggleDialog,
    } = this.props

    if (!customerProfile) return null

    return (
      <div className={classes.companyDetails}>
        <div className={classes.sectionHeader}>
          <Typography variant="h6">Company Information</Typography>
          {this.getNormalizedStatus(customerProfile.status) === 'Rejected' && (
            <Button
              onClick={() => {
                toggleDialog({ open: true, dialog: 'A2P10DLC' })
              }}
              variant="contained"
              className={`${classes.actionButton} ${classes.warningButton}`}
            >
              Fix Issues
            </Button>
          )}
        </div>
        <div style={{ padding: '0px 16px 16px' }}>
          <Typography variant="body1">
            <strong>Company Name:</strong> {customerProfile.friendlyName}
          </Typography>
          {user_email && (
            <Typography variant="body1">
              <strong>Contact Email:</strong> {user_email}
            </Typography>
          )}
          {user_phone_number && (
            <Typography variant="body1">
              <strong>Phone Number:</strong>{' '}
              {this.formatPhoneNumber(user_phone_number)}
            </Typography>
          )}
          <Typography
            variant="body1"
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <strong>Status:</strong>
            {this.renderStatusIcon(customerProfile.status)}
            <span
              style={{
                ...this.getStatusStyle(customerProfile.status),
                marginLeft: '4px',
              }}
            >
              {this.getNormalizedStatus(customerProfile.status)}
            </span>
          </Typography>
        </div>
      </div>
    )
  }

  render() {
    const { classes, trustProducts, useCases, toggleDialog } = this.props

    return (
      <div className={classes.complianceContainer}>
        <Typography variant="h4" className={classes.pageTitle}>
          Manage Phone Compliance
        </Typography>

        {this.renderCompanyDetails()}

        <Typography variant="h6" className={classes.sectionTitle}>
          Compliance Services
        </Typography>
        <List className={classes.listContainer}>
          {Object.entries(trustProducts).map(([sid, product], index) => {
            const normalizedStatus = this.getNormalizedStatus(product.status)
            const volumeType = getTrustProductVolumeType(
              product,
              this.props.a2p10dlc,
              index,
            )

            return (
              <ListItem
                key={sid}
                classes={{
                  container: classes.listItemContainer,
                }}
                className={classes.listItem}
              >
                <ListItemText
                  primary={
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {POLICY_FRIENDLY_NAMES[product.policySid] ||
                        'Unknown Service'}
                      {volumeType && (
                        <Typography
                          variant="caption"
                          style={{
                            marginLeft: 8,
                            backgroundColor: '#e0e0e0',
                            padding: '2px 6px',
                            borderRadius: 4,
                          }}
                        >
                          {volumeType}
                        </Typography>
                      )}
                    </div>
                  }
                  secondary={
                    <React.Fragment>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        Status:
                        {this.renderStatusIcon(product.status)}
                        <span
                          style={{
                            ...this.getStatusStyle(product.status),
                            marginLeft: '4px',
                          }}
                        >
                          {normalizedStatus}
                        </span>
                      </div>
                    </React.Fragment>
                  }
                  className={classes.listItemText}
                />
                <ListItemSecondaryAction>
                  <Button
                    onClick={() => {
                      normalizedStatus === 'Rejected'
                        ? toggleDialog({ open: true, dialog: 'A2P10DLC' })
                        : this.handleOpenDetails(product, 'trust-product')
                    }}
                    variant="contained"
                    className={`${classes.actionButton} ${
                      normalizedStatus === 'Rejected'
                        ? classes.warningButton
                        : ''
                    }`}
                  >
                    {normalizedStatus === 'Rejected'
                      ? 'Fix Issues'
                      : 'View Details'}
                  </Button>
                </ListItemSecondaryAction>
              </ListItem>
            )
          })}
        </List>

        <div className={classes.sectionHeader}>
          <Typography variant="h6">Use cases</Typography>
          <Button
            variant="contained"
            color="primary"
            className={classes.addButton}
            onClick={() =>
              toggleDialog({
                open: true,
                dialog: 'AddUseCase',
              })
            }
          >
            + Add New Use Case
          </Button>
        </div>

        <List className={classes.listContainer}>
          {Object.entries(useCases).map(([useCase, details]) => (
            <ListItem
              key={useCase}
              classes={{
                container: classes.listItemContainer,
              }}
              className={classes.listItem}
            >
              <ListItemText
                primary={`${(details.name || useCase).replace(/_/g, ' ')}`}
                secondary={
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    Status:
                    {this.renderStatusIcon(details.campaignStatus)}
                    <span
                      style={{
                        ...this.getStatusStyle(details.campaignStatus),
                        marginLeft: '4px',
                      }}
                    >
                      {this.getNormalizedStatus(details.campaignStatus)}
                    </span>
                  </div>
                }
                className={classes.listItemText}
              />
              <ListItemSecondaryAction>
                <Button
                  onClick={() => {
                    details.campaignStatus === 'FAILED'
                      ? toggleDialog({ open: true, dialog: 'A2P10DLC' })
                      : this.handleOpenDetails(details, 'use-case')
                  }}
                  variant="contained"
                  className={`${classes.actionButton} ${
                    details.campaignStatus === 'FAILED'
                      ? classes.warningButton
                      : ''
                  }`}
                >
                  {details.campaignStatus === 'FAILED'
                    ? 'Fix Issues'
                    : 'View Details'}
                </Button>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
        {this.renderDetailsDialog()}
      </div>
    )
  }
}

const mapStateToProps = state => {
  const a2p10dlc = Object.values(state.data.a2p10dlc || {})?.[0] || {}
  const customerProfile =
    Object.values(a2p10dlc.customerProfiles || {})?.[0] || {}
  const user_email = a2p10dlc.email || a2p10dlc.user_email || ''
  const user_phone_number =
    a2p10dlc.phone_number || a2p10dlc.user_phone_number || ''

  // Add 'WAITING' status to all useCaseDrafts
  const useCaseDrafts = Object.entries(a2p10dlc.useCaseDrafts || {}).reduce(
    (acc, [key, draft]) => {
      acc[key] = { ...draft, campaignStatus: 'WAITING' }
      return acc
    },
    {},
  )

  return {
    a2p10dlc,
    trustProducts: a2p10dlc.trustProducts || {},
    useCases: { ...a2p10dlc.campaigns, ...useCaseDrafts } || {},
    customerProfile,
    user_email,
    user_phone_number,
  }
}

const mapDispatchToProps = {
  toggleDialog,
}

// Compose the HOCs
export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(ManageCompliance))
